<template>
    <modal :show="show" @update:show="handleShowChange" modal-classes="modal-lg shopee-to-seller-modal">
        <h5 slot="header" class="modal-title">
            {{ $t("shopee_to_seller.title") }}
        </h5>

        <div>
            <div class="row mb-3">
                <div class="col-md-12">
                    <label class="form-control-label">{{ $t("name") }}</label>
                    <p class="font-weight-bold mb-1">{{ row ? row.name : "" }}</p>
                    <small class="text-muted d-block">
                        {{ $t("shopee_to_seller.item_id") }}:
                        <!-- #284: the id opens the listing in Seller Centre. Same
                             rule as the bulk results table — plain text when the
                             id is not usable, because <a href=""> would reload
                             the Backoffice and lose the preview. -->
                        <a
                            v-if="shopeeItemUrl(itemId)"
                            :href="shopeeItemUrl(itemId)"
                            target="_blank"
                            rel="noopener noreferrer"
                            :title="$t('shopee_bulk_sync.open_in_shopee')"
                        >{{ itemId }}</a>
                        <span v-else>{{ itemId || "-" }}</span>
                        &nbsp;·&nbsp;
                        {{ $t("shopee_to_seller.shop_id") }}: {{ shopId || "-" }}
                    </small>
                    <!-- #284: the shop's own name on Shopee, so "ส่งเข้า seller"
                         says which shop the listing is being read from. Quietly
                         "-" when the lookup fails; it never blocks the modal. -->
                    <small class="text-muted d-block">
                        {{ $t("shopee_bulk_sync.shop_name") }}: {{ shopeeShopName || "-" }}
                        <i class="fa fa-spinner fa-spin ml-1" v-if="shopInfoLoading"></i>
                    </small>
                </div>
            </div>

            <!-- Category picker. OPTIONAL since #282: leaving it empty makes the
                 BE resolve the category from Shopee's own (§6D.1), so the field
                 is an override, not a requirement. Still hidden on update: the
                 BE keeps the seller category it already has, and sending
                 category_id changes nothing (spec §6A). -->
            <div class="row mb-3" v-if="!isUpdate">
                <div class="col-md-12">
                    <label class="form-control-label">
                        {{ $t("shopee_to_seller.category") }}
                    </label>
                    <el-select
                        v-model="categoryId"
                        :placeholder="$t('shopee_category_map.auto_placeholder')"
                        filterable
                        clearable
                        class="w-100"
                    >
                        <el-option
                            v-for="cat in categoryList"
                            :key="cat.id"
                            :label="cat.name_th || cat.name"
                            :value="cat.id"
                        ></el-option>
                    </el-select>
                    <small class="text-muted d-block">{{ $t("shopee_category_map.modal_hint") }}</small>
                    <small class="text-muted">
                        {{ $t("shopee_to_seller.category_hint") }}
                        <router-link
                            :to="{ name: 'AppCatalogShopeeCategoryMap' }"
                            target="_blank"
                            class="ml-1"
                        >
                            {{ $t("shopee_category_map.modal_link") }}
                        </router-link>
                    </small>
                </div>
            </div>
            <div class="row mb-3" v-else>
                <div class="col-md-12">
                    <div class="alert alert-info py-2 px-3 mb-0">
                        <i class="fa fa-info-circle mr-1"></i>
                        {{ $t("shopee_to_seller.update_category_kept") }}
                    </div>
                </div>
            </div>

            <!-- 422 rejects -->
            <div class="row mb-3" v-if="rejects.length">
                <div class="col-md-12">
                    <div class="alert alert-danger py-2 px-3">
                        <strong>{{ $t("shopee_to_seller.rejected_title") }}</strong>
                        <ul class="mb-0 mt-2 pl-4">
                            <!-- An UNKNOWN reason_code prints the backend's own
                                 message (rejectText), never an empty bullet. -->
                            <li v-for="(rj, idx) in rejects" :key="idx">
                                {{ rejectText(rj) }}
                                <span class="text-muted">
                                    ({{ $t("shopee_to_seller.model_id") }}: {{ rj.model_id }})
                                </span>
                                <span v-if="rejectDetail(rj)" class="text-muted">
                                    — {{ rejectDetail(rj) }}
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- other errors -->
            <div class="row mb-3" v-if="errorKey">
                <div class="col-md-12">
                    <div class="alert alert-danger py-2 px-3 mb-0">
                        <i class="fa fa-exclamation-triangle mr-1"></i>
                        {{ $t(errorKey) }}
                        <span v-if="errorDetail" class="d-block small">{{ errorDetail }}</span>
                    </div>
                </div>
            </div>

            <!-- preview -->
            <div v-if="preview">
                <hr />
                <div class="row mb-3">
                    <div class="col-md-6">
                        <label class="form-control-label">{{ $t("shopee_to_seller.preview_name") }}</label>
                        <p class="font-weight-bold mb-0">{{ preview.product ? preview.product.name : "" }}</p>
                    </div>
                    <div class="col-md-3">
                        <label class="form-control-label">{{ $t("shopee_to_seller.image_count") }}</label>
                        <p class="font-weight-bold mb-0">{{ preview.product ? preview.product.image_count : 0 }}</p>
                    </div>
                    <div class="col-md-3">
                        <label class="form-control-label">
                            {{ $t("shopee_to_seller.description_image_count") }}
                        </label>
                        <p class="font-weight-bold mb-0">
                            {{ preview.product ? preview.product.description_image_count : 0 }}
                        </p>
                    </div>
                </div>

                <!-- #282: which seller category this product will land in, and
                     how that was decided. `auto_new` / would_create is the one
                     worth stopping at — it is the only outcome that adds a
                     category to the seller's list. -->
                <div class="row mb-3" v-if="categoryResolution">
                    <div class="col-md-12">
                        <label class="form-control-label">
                            {{ $t("shopee_category_map.preview_category") }}
                        </label>
                        <p class="mb-0">
                            <span class="font-weight-bold">{{ categoryResolution.name }}</span>
                            <el-tag size="mini" class="ml-1" :type="categoryTag.type">
                                {{ $t(categoryTag.key) }}
                            </el-tag>
                            <el-tag size="mini" type="warning" class="ml-1" v-if="categoryWouldCreate">
                                {{ $t("shopee_category_map.would_create") }}
                            </el-tag>
                        </p>
                    </div>
                </div>

                <div class="row mb-3" v-if="optionGroups.length">
                    <div class="col-md-12">
                        <label class="form-control-label">{{ $t("shopee_to_seller.option_groups") }}</label>
                        <div v-for="(grp, gi) in optionGroups" :key="gi" class="mb-1">
                            <strong>{{ grp.name }}</strong>:
                            <span v-for="(val, vi) in grp.values || []" :key="vi" class="mr-1">
                                <el-tag size="mini" type="info">{{ val }}</el-tag>
                            </span>
                        </div>
                    </div>
                </div>

                <label class="form-control-label">
                    {{ $t("shopee_to_seller.sku_table") }} ({{ skus.length }})
                </label>
                <el-table :data="skus" header-row-class-name="thead-light" max-height="320">
                    <el-table-column prop="sku_code" :label="$t('shopee_to_seller.col_sku_code')" min-width="160">
                    </el-table-column>
                    <el-table-column prop="sku_name" :label="$t('shopee_to_seller.col_sku_name')" min-width="160">
                    </el-table-column>
                    <el-table-column prop="price" :label="$t('shopee_to_seller.col_price')" width="110" align="right">
                    </el-table-column>
                    <el-table-column :label="$t('shopee_to_seller.col_action')" width="130" align="center">
                        <template slot-scope="{ row: sku }">
                            <el-tag size="mini" :type="actionTagType(sku.action)">
                                {{ $t(actionKey(sku.action)) }}
                            </el-tag>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- Warnings belong to the PREVIEW, not to the write: a dry run
                     already reports wms_unmatched, and the operator must read it
                     before confirming. Gating this on doneProductId would hide
                     exactly the warning that is still actionable. -->
                <div class="mt-3" v-if="warnings.length">
                    <div class="alert alert-warning py-2 px-3 mb-0">
                        <strong>{{ $t("shopee_to_seller.warnings_title") }}</strong>
                        <span class="text-muted small ml-1" v-if="isDryRunPreview">
                            ({{ $t("shopee_to_seller.warnings_dry_run") }})
                        </span>
                        <ul class="mb-0 mt-2 pl-4">
                            <li v-for="(w, idx) in warnings" :key="idx">
                                <span v-if="w.code">[{{ w.code }}] </span>{{ w.message }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- success -->
            <div class="row mt-3" v-if="doneProductId">
                <div class="col-md-12">
                    <div class="alert alert-success py-2 px-3 mb-0">
                        <i class="fa fa-check-circle mr-1"></i>
                        {{ $t("shopee_to_seller.success") }}
                        <a href="#" class="ml-2 font-weight-bold" @click.prevent="openInCatalog">
                            {{ $t("shopee_to_seller.open_in_catalog") }}
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <template slot="footer">
            <button
                type="button"
                class="btn btn-md btn-primary"
                :disabled="loading || !itemId"
                @click="handleCheck()"
                v-if="!doneProductId"
            >
                <i class="fa fa-spinner fa-spin mr-1" v-if="loading"></i>
                {{ $t("shopee_to_seller.btn_check") }}
            </button>
            <button
                type="button"
                class="btn btn-md btn-success"
                :disabled="loading || !canConfirm"
                @click="handleConfirm()"
                v-if="!doneProductId"
            >
                <i class="fa fa-spinner fa-spin mr-1" v-if="loading"></i>
                {{ $t("shopee_to_seller.btn_confirm") }}
            </button>
            <button type="button" class="btn btn-md btn-link ml-auto" @click="close()">
                {{ $t("close") }}
            </button>
        </template>
    </modal>
</template>

<script>
// #260 — "ส่งเข้า seller" modal for /admin/product_platform Shopee rows.
// Spec: docs/specs/2026-09-22-shopee-master-product-sync-design.md §6A (FE).
//
// The two-step contract is load-bearing and asserted by
// tests/unit/shopeeToSellerSource.spec.js: "ตรวจสอบ" ALWAYS posts
// dry_run:true first, and "ยืนยันส่งเข้า seller" (dry_run:false) is only
// reachable once a dry run has come back clean.
import { Select, Option, Table, TableColumn, Tag } from "element-ui";
import { ShopeeService } from "@/resource";
import {
    shopeeItemId,
    shopeeShopId,
    toSellerBody,
    rejectDisplayText,
    rejectDetailText,
    skuActionTagType,
    skuActionI18nKey,
    toSellerErrorI18nKey,
    toSellerErrorDetail,
    toSellerWarnings,
    rejectsFromError,
    shopeeSellerProductUrl
} from "@/utils/shopeeToSeller";
import { categoryResolutionTag } from "@/utils/shopeeCategoryMap";

export default {
    name: "shopee-to-seller-modal",
    components: {
        // Element UI is NOT registered globally in this app (only Input,
        // Tooltip and Popover are — src/plugins/globalComponents.js), so every
        // el-* used above has to be registered here.
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag
    },
    props: {
        show: { type: Boolean, default: false },
        row: { type: Object, default: null },
        // Whether this Shopee item already has a seller product. Drives
        // "create vs update": on update the category picker is hidden.
        linked: { type: Boolean, default: false }
    },
    data() {
        return {
            categoryId: null,
            loading: false,
            preview: null,
            rejects: [],
            errorKey: "",
            errorDetail: "",
            doneProductId: 0,
            // #284 — GET .../shops/:shop_id/info, once per shop id. Null means
            // "not loaded, or the lookup failed"; the header shows "-" either way.
            shopInfo: null,
            shopInfoShopId: "",
            shopInfoLoading: false
        };
    },
    computed: {
        categoryList() {
            return this.$store.getters["AppCatalogCategory/list"] || [];
        },
        itemId() {
            return shopeeItemId(this.row);
        },
        shopId() {
            return shopeeShopId(this.row);
        },
        // #284: shopeeShopId returns a NUMBER (0 when absent), so the cache key
        // and the request path both go through the string form.
        shopIdText() {
            return this.shopId ? String(this.shopId) : "";
        },
        // The shop's name on Shopee. "" while loading and "" after a failure.
        shopeeShopName() {
            return (this.shopInfo && this.shopInfo.shop_name) || "";
        },
        isUpdate() {
            if (this.linked) return true;
            return !!(this.preview && this.preview.action === "update");
        },
        skus() {
            return (this.preview && this.preview.skus) || [];
        },
        optionGroups() {
            return (this.preview && this.preview.product && this.preview.product.option_groups) || [];
        },
        // Warnings come from whichever run produced the preview — the dry run
        // carries them too (wms_unmatched).
        warnings() {
            return toSellerWarnings(this.preview);
        },
        isDryRunPreview() {
            return !!(this.preview && this.preview.dry_run === true);
        },
        // The confirm button stays dead until a dry run has returned a preview
        // with no rejects — dry_run:true before dry_run:false, always.
        //
        // It is NOT gated on a category any more (#282). Until §6D a create
        // without category_id came back 400 category_required, so the button had
        // to refuse it; now the BE resolves the category from Shopee itself and
        // that 400 cannot happen, so demanding one here would only stop the
        // operator from using the automatic path the feature exists for.
        canConfirm() {
            if (!this.preview || this.rejects.length !== 0) return false;
            return true;
        },
        // §6D.3's resolution object, present on both the dry run and the write.
        categoryResolution() {
            return (this.preview && this.preview.category) || null;
        },
        categoryTag() {
            return categoryResolutionTag(this.categoryResolution);
        },
        categoryWouldCreate() {
            const res = this.categoryResolution;
            if (!res) return false;
            return res.would_create === true || res.source === "auto_new";
        }
    },
    watch: {
        show(val) {
            if (val) this.reset();
        }
    },
    methods: {
        // #284 — Seller Centre deep link, or "" for an id that is not usable.
        shopeeItemUrl: shopeeSellerProductUrl,
        // #284 — the same lookup ShopeeBulkSyncModal does, with the same rules:
        // once per shop id, never awaited, never turned into an errorKey. This
        // endpoint is decoration on a modal that writes products; a failed name
        // must not put a red box over a working preview.
        loadShopInfo() {
            var self = this;
            var shopId = this.shopIdText;
            if (shopId === "") {
                this.shopInfo = null;
                this.shopInfoShopId = "";
                this.shopInfoLoading = false;
                return Promise.resolve();
            }
            if (this.shopInfoShopId === shopId && this.shopInfo) return Promise.resolve();
            this.shopInfo = null;
            this.shopInfoShopId = shopId;
            this.shopInfoLoading = true;
            return ShopeeService.ShopInfo(shopId)
                .then(function(resp) {
                    self.shopInfoLoading = false;
                    // The operator may have opened another row's modal meanwhile.
                    if (self.shopIdText !== shopId) return;
                    self.shopInfo = (resp && resp.data) || null;
                })
                .catch(function() {
                    self.shopInfoLoading = false;
                    if (self.shopIdText !== shopId) return;
                    self.shopInfo = null;
                    // Cleared so the next open retries.
                    self.shopInfoShopId = "";
                });
        },
        rejectText(reject) {
            return rejectDisplayText(reject, this.$t.bind(this));
        },
        rejectDetail: rejectDetailText,
        actionTagType: skuActionTagType,
        actionKey: skuActionI18nKey,
        reset() {
            this.categoryId = null;
            this.loading = false;
            this.preview = null;
            this.rejects = [];
            this.errorKey = "";
            this.errorDetail = "";
            this.doneProductId = 0;
            // Categories are the same list the app-catalog product form uses.
            this.$store.dispatch("AppCatalogCategory/fetchList", {});
            // #284: the Shopee shop name. Deliberately NOT cleared above — it is
            // keyed on the shop id, so two rows of the same shop share one call.
            this.loadShopInfo();
        },
        handleShowChange(val) {
            this.$emit("update:show", val);
        },
        close() {
            this.$emit("update:show", false);
        },
        clearMessages() {
            this.rejects = [];
            this.errorKey = "";
            this.errorDetail = "";
        },
        // ตรวจสอบ = dry run. Never writes on the seller side.
        handleCheck() {
            return this.send(true);
        },
        // ยืนยันส่งเข้า seller = the real write. Guarded by canConfirm so it
        // cannot be the first call.
        handleConfirm() {
            if (!this.canConfirm) return Promise.resolve();
            return this.send(false);
        },
        send(dryRun) {
            var self = this;
            this.clearMessages();
            this.loading = true;
            var body = toSellerBody(this.shopId, this.categoryId, dryRun);
            return ShopeeService.ToSeller(this.itemId, body)
                .then(function(resp) {
                    self.loading = false;
                    var data = (resp && resp.data) || {};
                    // The BE answers the same envelope for both runs.
                    self.preview = data;
                    if (dryRun === false) {
                        self.doneProductId = Number(data.product_id) || 0;
                        self.$emit("synced", {
                            item_id: self.itemId,
                            product_id: self.doneProductId
                        });
                    }
                    return resp;
                })
                .catch(function(err) {
                    self.loading = false;
                    var rejects = rejectsFromError(err);
                    if (rejects.length) {
                        self.rejects = rejects;
                        self.preview = null;
                        return;
                    }
                    self.preview = null;
                    self.errorKey = toSellerErrorI18nKey(err);
                    self.errorDetail = toSellerErrorDetail(err);
                });
        },
        openInCatalog() {
            this.$router.push({
                name: "AppCatalogProductEdit",
                params: { id: String(this.doneProductId) }
            });
        }
    }
};
</script>

<style>
.shopee-to-seller-modal .modal-body {
    max-height: 70vh;
    overflow-y: auto;
}
</style>
